import { useCallback, useEffect, useState } from 'react';
import { RedditDestination, tendApi } from '../api/tendApi';

/**
 * Per-brand Reddit destination list: module-level cache + pub-sub (the
 * useLinkSettings mechanism — cards share one fetch per brand). The list
 * is server-validated on add (Ayrshare redditExists + flair caching);
 * the connected profile is implicit and arrives as `username`.
 */

interface RedditEntry {
  destinations: RedditDestination[];
  username: string | null;
}

const cache = new Map<number, RedditEntry>();
const listeners = new Map<number, Set<() => void>>();
const loads = new Map<number, Promise<void>>();

const notify = (brandId: number) => {
  listeners.get(brandId)?.forEach((cb) => { try { cb(); } catch (e) { /* */ } });
};

async function loadEntry(brandId: number): Promise<void> {
  const existing = loads.get(brandId);
  if (existing) return existing;
  const p = (async () => {
    const settings = await tendApi.getBrandOverlaySettings(brandId);
    if (!settings) return;
    cache.set(brandId, {
      destinations: (settings as any).reddit_destinations || [],
      username: (settings as any).reddit_username || null,
    });
    notify(brandId);
  })();
  loads.set(brandId, p);
  try { await p; } finally { loads.delete(brandId); }
}

export interface RedditDestinationsState {
  loaded: boolean;
  /** Validated subreddit entries (flairs cached per entry). */
  destinations: RedditDestination[];
  /** Connected Reddit username — labels the implicit profile default. */
  username: string | null;
  /** Validate + add; throws with the server's reason on rejection. */
  add: (name: string) => Promise<RedditDestination>;
  remove: (name: string) => Promise<void>;
}

export function useRedditDestinations(brandId: number | undefined): RedditDestinationsState {
  const [, force] = useState(0);

  useEffect(() => {
    if (!brandId) return;
    const cb = () => force((n) => n + 1);
    let set = listeners.get(brandId);
    if (!set) { set = new Set(); listeners.set(brandId, set); }
    set.add(cb);
    if (!cache.has(brandId)) loadEntry(brandId);
    return () => { set?.delete(cb); };
  }, [brandId]);

  const entry = brandId ? cache.get(brandId) : undefined;

  const add = useCallback(async (name: string): Promise<RedditDestination> => {
    if (!brandId) throw new Error('No brand');
    const result = await tendApi.addRedditDestination(brandId, name);
    cache.set(brandId, {
      destinations: result.reddit_destinations,
      username: cache.get(brandId)?.username || null,
    });
    notify(brandId);
    return result.destination;
  }, [brandId]);

  const remove = useCallback(async (name: string): Promise<void> => {
    if (!brandId) return;
    const result = await tendApi.removeRedditDestination(brandId, name);
    cache.set(brandId, {
      destinations: result.reddit_destinations,
      username: cache.get(brandId)?.username || null,
    });
    notify(brandId);
  }, [brandId]);

  return {
    loaded: !!entry,
    destinations: entry?.destinations || [],
    username: entry?.username || null,
    add,
    remove,
  };
}
